import * as React from 'react';
import {useState, useTransition} from 'react';
import {graphql, useLazyLoadQuery, useMutation} from 'react-relay';
import {Link, useNavigate, useParams} from 'react-router-dom';
import {ArrowLeft, Check, Printer, X} from 'lucide-react';
import ScanInput from './ScanInput';
import {OpsReservationPageQuery} from './__generated__/OpsReservationPageQuery.graphql';
import {OpsReservationPageConfirmMutation} from './__generated__/OpsReservationPageConfirmMutation.graphql';
import {OpsReservationPageCancelMutation} from './__generated__/OpsReservationPageCancelMutation.graphql';
import {OpsReservationPageCheckOutMutation} from './__generated__/OpsReservationPageCheckOutMutation.graphql';
import {OpsReservationPageAddLineMutation} from './__generated__/OpsReservationPageAddLineMutation.graphql';
import {OpsReservationPageRemoveLineMutation} from './__generated__/OpsReservationPageRemoveLineMutation.graphql';
import {OpsReservationPagePickMutation} from './__generated__/OpsReservationPagePickMutation.graphql';
import {OpsReservationPageReturnMutation} from './__generated__/OpsReservationPageReturnMutation.graphql';
import {OpsReservationPageUpdateMutation} from './__generated__/OpsReservationPageUpdateMutation.graphql';
import {
    dangerButton, formatDateTime, fromLocalInput, ghostButton, inputClass, primaryButton, statusClass, statusLabel, toLocalInput,
} from './lib';

const QUERY = graphql`
    query OpsReservationPageQuery($id: ID!) {
        reservation(id: $id) {
            id
            status
            startsAt
            endsAt
            days
            notes
            quotedTotal
            editable
            checkedOutAt
            returnedAt
            customer { id name email phone }
            lines {
                id
                asset { assetTag name category }
                holds { id }
            }
            holds {
                id
                picked
                returned
                asset { assetTag name category kit { assetTag } }
            }
        }
    }`;

// Every mutation returns the same shape so one handler can process them all.
const CONFIRM = graphql`mutation OpsReservationPageConfirmMutation($input: ConfirmReservationInput!) { confirmReservation(input: $input) { reservation { id } userErrors { message assetTag } } }`;
const CANCEL = graphql`mutation OpsReservationPageCancelMutation($input: CancelReservationInput!) { cancelReservation(input: $input) { reservation { id } userErrors { message assetTag } } }`;
const CHECK_OUT = graphql`mutation OpsReservationPageCheckOutMutation($input: CheckOutReservationInput!) { checkOutReservation(input: $input) { reservation { id } userErrors { message assetTag } } }`;
const ADD_LINE = graphql`mutation OpsReservationPageAddLineMutation($input: AddReservationLineInput!) { addReservationLine(input: $input) { reservation { id } userErrors { message assetTag } } }`;
const REMOVE_LINE = graphql`mutation OpsReservationPageRemoveLineMutation($input: RemoveReservationLineInput!) { removeReservationLine(input: $input) { reservation { id } userErrors { message assetTag } } }`;
const PICK = graphql`mutation OpsReservationPagePickMutation($input: PickAssetInput!) { pickAsset(input: $input) { reservation { id } userErrors { message assetTag } } }`;
const RETURN = graphql`mutation OpsReservationPageReturnMutation($input: ReturnAssetInput!) { returnAsset(input: $input) { reservation { id } userErrors { message assetTag } } }`;
const UPDATE = graphql`mutation OpsReservationPageUpdateMutation($input: UpdateReservationInput!) { updateReservation(input: $input) { reservation { id } userErrors { message assetTag } } }`;
type Payload = { reservation: { id: string } | null; userErrors: ReadonlyArray<{ message: string }> } | null | undefined;

// Resolves to an error message (or null) so ScanInput and buttons can share it.
const settle = (payload: Payload): string | null =>
    payload?.reservation ? null : payload?.userErrors.map((u) => u.message).join('; ') || 'Something went wrong';

type ScanMode = 'add' | 'pick' | 'return' | null;

const OpsReservationPage: React.FC = () => {
    const {id = ''} = useParams();
    const navigate = useNavigate();
    const [fetchKey, setFetchKey] = useState(0);
    // Refetching inside a transition keeps the page (and the scan input's focus) mounted.
    const [, startTransition] = useTransition();
    const data = useLazyLoadQuery<OpsReservationPageQuery>(QUERY, {id: decodeURIComponent(id)}, {fetchKey, fetchPolicy: 'network-only'});
    const r = data.reservation;

    const [confirmReservation] = useMutation<OpsReservationPageConfirmMutation>(CONFIRM);
    const [cancel] = useMutation<OpsReservationPageCancelMutation>(CANCEL);
    const [checkOut] = useMutation<OpsReservationPageCheckOutMutation>(CHECK_OUT);
    const [addLine] = useMutation<OpsReservationPageAddLineMutation>(ADD_LINE);
    const [removeLine] = useMutation<OpsReservationPageRemoveLineMutation>(REMOVE_LINE);
    const [pick] = useMutation<OpsReservationPagePickMutation>(PICK);
    const [returnAsset] = useMutation<OpsReservationPageReturnMutation>(RETURN);
    const [update] = useMutation<OpsReservationPageUpdateMutation>(UPDATE);

    const [banner, setBanner] = useState<string | null>(null);
    const [modeOverride, setModeOverride] = useState<ScanMode>(null);
    const [editingDates, setEditingDates] = useState(false);
    const [confirmingCancel, setConfirmingCancel] = useState(false);
    const [dates, setDates] = useState({startsAt: '', endsAt: ''});

    if (!r) {
        return <p className="text-gray-500">Reservation not found. <Link to="/ops/reservations" className="text-lumina-purple">Back to list</Link></p>;
    }

    const refresh = () => startTransition(() => setFetchKey((k) => k + 1));

    // Wraps a Relay commit into a promise of error-or-null and refreshes the page query on success.
    const run = <T, >(commit: (config: any) => void, input: Record<string, unknown>, pick: (res: T) => Payload) =>
        new Promise<string | null>((resolve) => {
            commit({
                variables: {input: {reservationId: r.id, ...input}},
                onCompleted: (res: T) => {
                    const error = settle(pick(res));
                    if (!error) refresh();
                    resolve(error);
                },
                onError: (err: Error) => resolve(err.message),
            });
        });

    const act = async (p: Promise<string | null>) => setBanner(await p);

    const picked = r.holds.filter((h) => h.picked).length;
    const returned = r.holds.filter((h) => h.returned).length;
    const allPicked = r.holds.length > 0 && picked === r.holds.length;

    const defaultMode: ScanMode =
        r.status === 'DRAFT' || r.status === 'REQUESTED' ? 'add' :
        r.status === 'CONFIRMED' || r.status === 'PICKED' ? 'pick' :
        r.status === 'CHECKED_OUT' ? 'return' : null;
    const mode = modeOverride ?? defaultMode;

    const onScan = (tag: string) => {
        if (mode === 'add') return run(addLine, {assetTag: tag}, (res: any) => res.addReservationLine);
        if (mode === 'pick') return run(pick, {assetTag: tag}, (res: any) => res.pickAsset);
        if (mode === 'return') return run(returnAsset, {assetTag: tag}, (res: any) => res.returnAsset);
        return Promise.resolve('Nothing to scan for in this state');
    };

    const saveDates = () => act(run(update, {
        startsAt: fromLocalInput(dates.startsAt), endsAt: fromLocalInput(dates.endsAt),
    }, (res: any) => res.updateReservation).then((e) => { if (!e) setEditingDates(false); return e; }));

    return (
        <div>
            <Link to="/ops/reservations" className="text-xs text-gray-500 hover:text-white flex items-center gap-1 mb-4">
                <ArrowLeft className="w-3 h-3"/> All reservations
            </Link>

            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6">
                <div>
                    <div className="flex items-center gap-3 mb-1">
                        <span className={`text-[10px] font-bold px-2 py-0.5 border ${statusClass[r.status]}`}>{statusLabel(r.status)}</span>
                        <h1 className="text-3xl font-display font-bold">{r.customer.name}</h1>
                    </div>
                    <p className="text-gray-500 text-sm">{[r.customer.email, r.customer.phone].filter(Boolean).join(' · ')}</p>
                    {editingDates ? (
                        <div className="flex items-center gap-2 mt-2">
                            <input type="datetime-local" className={inputClass} value={dates.startsAt}
                                   onChange={(e) => setDates({...dates, startsAt: e.target.value})}/>
                            <span className="text-gray-600">→</span>
                            <input type="datetime-local" className={inputClass} value={dates.endsAt}
                                   onChange={(e) => setDates({...dates, endsAt: e.target.value})}/>
                            <button onClick={saveDates} className={primaryButton}>Save</button>
                            <button onClick={() => setEditingDates(false)} className={ghostButton}>Cancel</button>
                        </div>
                    ) : (
                        <p className="text-gray-300 mt-2">
                            {formatDateTime(r.startsAt)} → {formatDateTime(r.endsAt)}
                            <span className="text-gray-600"> · {r.days} day{r.days === 1 ? '' : 's'}</span>
                            {r.quotedTotal && <span className="text-lumina-accent font-display"> · ${r.quotedTotal}</span>}
                            {r.editable && (
                                <button onClick={() => { setDates({startsAt: toLocalInput(new Date(r.startsAt)), endsAt: toLocalInput(new Date(r.endsAt))}); setEditingDates(true); }}
                                        className="ml-3 text-xs text-gray-500 hover:text-white">edit</button>
                            )}
                        </p>
                    )}
                    {r.notes && <p className="text-gray-400 text-sm mt-2 whitespace-pre-line">{r.notes}</p>}
                </div>

                <div className="flex flex-wrap gap-2">
                    <a href={`/ops/reservations/${encodeURIComponent(r.id)}/pick_list`} target="_blank" rel="noreferrer"
                       className={`${ghostButton} flex items-center gap-2`}><Printer className="w-3 h-3"/> Pick list</a>
                    {(r.status === 'DRAFT' || r.status === 'REQUESTED') && (
                        <button onClick={() => act(run(confirmReservation, {}, (res: any) => res.confirmReservation))}
                                disabled={r.lines.length === 0} className={primaryButton}>Confirm</button>
                    )}
                    {(r.status === 'CONFIRMED' || r.status === 'PICKED') && (
                        <button onClick={() => act(run(checkOut, {}, (res: any) => res.checkOutReservation))}
                                disabled={!allPicked} className={primaryButton} title={allPicked ? '' : 'Pick everything first'}>
                            Check out
                        </button>
                    )}
                    {!['CHECKED_OUT', 'RETURNED', 'CANCELLED'].includes(r.status) && (
                        confirmingCancel ? (
                            <>
                                <button onClick={() => { setConfirmingCancel(false); act(run(cancel, {}, (res: any) => res.cancelReservation)); }}
                                        className={dangerButton}>Yes, release the gear</button>
                                <button onClick={() => setConfirmingCancel(false)} className={ghostButton}>Keep it</button>
                            </>
                        ) : (
                            <button onClick={() => setConfirmingCancel(true)} className={dangerButton}>Cancel</button>
                        )
                    )}
                </div>
            </div>

            {banner && (
                <div className="mb-4 px-4 py-2 border border-red-500/40 bg-red-950/30 text-red-300 text-sm flex justify-between">
                    <span>{banner}</span>
                    <button onClick={() => setBanner(null)}><X className="w-4 h-4"/></button>
                </div>
            )}

            {mode && (
                <section className="mb-8 p-4 border border-gray-800 bg-black/40">
                    <div className="flex items-center justify-between mb-2">
                        <h2 className="text-xs uppercase tracking-widest text-gray-500">
                            {mode === 'add' && 'Add gear'}
                            {mode === 'pick' && `Pick · ${picked}/${r.holds.length}`}
                            {mode === 'return' && `Return · ${returned}/${r.holds.length} back`}
                        </h2>
                        {r.editable && r.status !== 'DRAFT' && (
                            <button onClick={() => setModeOverride(mode === 'add' ? 'pick' : 'add')} className="text-xs text-gray-400 hover:text-white">
                                switch to {mode === 'add' ? 'picking' : 'adding gear'}
                            </button>
                        )}
                    </div>
                    <ScanInput
                        placeholder={
                            mode === 'add' ? 'Scan a kit or item to add…' :
                            mode === 'pick' ? 'Scan each item as you pull it…' : 'Scan each item as it comes back…'
                        }
                        onScan={onScan}
                    />
                </section>
            )}

            <section className="mb-8">
                <h2 className="text-xs uppercase tracking-widest text-gray-500 mb-2">Requested</h2>
                {r.lines.length === 0 && <p className="text-gray-500 text-sm">Nothing yet — scan or type a tag above.</p>}
                <ul className="border border-gray-800 divide-y divide-gray-900">
                    {r.lines.map((line) => (
                        <li key={line.id} className="flex items-center gap-4 px-3 py-2">
                            <span className="font-mono text-xs text-gray-400 w-36">{line.asset.assetTag}</span>
                            <span className="text-white text-sm flex-1">
                                {line.asset.name}
                                {line.holds.length > 1 && <span className="text-gray-500 text-xs"> · {line.holds.length} pieces</span>}
                            </span>
                            <span className="text-xs text-gray-500">{line.asset.category.replace(/_/g, ' ')}</span>
                            {r.editable && (
                                <button onClick={() => act(run(removeLine, {assetTag: line.asset.assetTag}, (res: any) => res.removeReservationLine))}
                                        className="text-gray-500 hover:text-red-400" title="Remove"><X className="w-4 h-4"/></button>
                            )}
                        </li>
                    ))}
                </ul>
            </section>

            {r.holds.length > 0 && (
                <section>
                    <h2 className="text-xs uppercase tracking-widest text-gray-500 mb-2">Pick list · every physical piece</h2>
                    <table className="w-full text-sm border border-gray-800">
                        <tbody className="divide-y divide-gray-900">
                        {r.holds.map((h) => (
                            <tr key={h.id} className={h.returned ? 'opacity-50' : ''}>
                                <td className="px-3 py-2 w-8">
                                    {h.returned ? <Check className="w-4 h-4 text-emerald-400"/> :
                                        h.picked ? <Check className="w-4 h-4 text-lumina-accent"/> :
                                            <span className="block w-3 h-3 border border-gray-700"/>}
                                </td>
                                <td className="px-3 py-2 font-mono text-xs text-gray-300 whitespace-nowrap">{h.asset.assetTag}</td>
                                <td className="px-3 py-2 text-white">{h.asset.name}</td>
                                <td className="px-3 py-2 text-xs text-gray-500">{h.asset.kit ? `in ${h.asset.kit.assetTag}` : ''}</td>
                                <td className="px-3 py-2 text-xs text-gray-500 text-right">
                                    {h.returned ? 'returned' : h.picked ? 'picked' : ''}
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </section>
            )}

            {r.status === 'CANCELLED' && (
                <p className="mt-6 text-sm text-gray-500">Cancelled. <button onClick={() => navigate('/ops/reservations/new')} className="text-lumina-purple">Start a new one</button></p>
            )}
        </div>
    );
};

export default OpsReservationPage;
